import type { EnvironmentView, RunView } from "@cosmosai/api-client";
import { useTranslation } from "react-i18next";
import { RouteLink } from "../../app/route-link";
import { useFormat } from "../../i18n/format";
import { Badge } from "../../ui/badge";
import { EnvBadge } from "../../ui/env-badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { routes } from "../context";

/** Badge variants by run status. */
export const RUN_VARIANT = {
  queued: "default",
  running: "info",
  done: "ok",
  failed: "crit",
  cancelled: "default",
} as const;

/** The question of a run of the rag type, else nothing (runs are rendered as text only). */
export const questionOf = (run: RunView): string =>
  typeof run.input.question === "string" ? run.input.question : "";

/** How long a run took, from its start to its end; null while it has not ended. */
export const durationOf = (run: RunView): number | null =>
  run.startedAt && run.finishedAt ? Date.parse(run.finishedAt) - Date.parse(run.startedAt) : null;

/** Runs, most recent first: id, agent, environment, status, question, created, duration, cost. */
export function RunsTable({
  workspace,
  project,
  runs,
  environments,
}: {
  workspace: string;
  project: string;
  runs: RunView[];
  environments: EnvironmentView[];
}) {
  const { t } = useTranslation();
  const { relative, dateTime, duration, cost } = useFormat();
  const tierOf = (name: string) =>
    environments.find((env) => env.name === name)?.tier ?? "development";
  return (
    <div className="rounded-md border border-line bg-surface">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("runs.columns.run")}</TableHead>
            <TableHead>{t("runs.columns.agent")}</TableHead>
            <TableHead>{t("runs.columns.environment")}</TableHead>
            <TableHead>{t("runs.columns.status")}</TableHead>
            <TableHead>{t("runs.columns.question")}</TableHead>
            <TableHead>{t("runs.columns.created")}</TableHead>
            <TableHead className="text-right">{t("runs.columns.duration")}</TableHead>
            <TableHead className="text-right">{t("runs.columns.cost")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run) => {
            const took = durationOf(run);
            const question = questionOf(run);
            return (
              <TableRow key={run.id} data-testid={`run-row-${run.id}`}>
                <TableCell>
                  <RouteLink
                    to={routes.run(workspace, project, run.id)}
                    className="font-mono text-sm"
                  >
                    {run.id.slice(0, 8)}
                  </RouteLink>
                </TableCell>
                <TableCell className="whitespace-nowrap">{run.agent}</TableCell>
                <TableCell>
                  <EnvBadge tier={tierOf(run.environment)} name={run.environment} />
                </TableCell>
                <TableCell>
                  <Badge variant={RUN_VARIANT[run.status]}>{t(`runs.status.${run.status}`)}</Badge>
                </TableCell>
                <TableCell className="max-w-[28ch] truncate" title={question}>
                  {question || "—"}
                </TableCell>
                <TableCell>
                  <time dateTime={run.createdAt} title={dateTime(run.createdAt)}>
                    {relative(run.createdAt)}
                  </time>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {took === null ? "—" : duration(took)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {run.cost ? cost(Number(run.cost.amount), run.cost.currency) : "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
